import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { GroupSaveOutcome, GroupSaveResult } from '@actions/Admin/Group/GroupSaveMessage';

export interface GroupSaveResultModalProps {
  result: GroupSaveResult | null;
  onClose: () => void;
  /** Closes the team editor and reloads the list — offered when the save may or may not have landed. */
  onRefresh: () => void;
}

const outcomeIcon: Record<GroupSaveOutcome, React.ReactNode> = {
  saved: <CheckCircleOutlineIcon color={'success'} />,
  savedWithProblems: <WarningAmberIcon color={'warning'} />,
  failed: <ErrorOutlineIcon color={'error'} />,
  unknown: <HelpOutlineIcon color={'warning'} />,
};

/**
 * The outcome of saving a team. It sits on top of the team editor and stays open until the
 * admin acknowledges it, so a failure can't go unnoticed.
 */
export const GroupSaveResultModal: React.FC<GroupSaveResultModalProps> = ({ result, onClose, onRefresh }) => {
  if (!result) return null;

  const teamSaved = result.outcome === 'saved' || result.outcome === 'savedWithProblems';

  return (
    <Dialog
      open={true}
      maxWidth={'sm'}
      fullWidth={true}
      // Only the buttons dismiss it; a stray click on the backdrop shouldn't hide the result unread
      onClose={(_e, reason) => reason !== 'backdropClick' && onClose()}
      aria-labelledby="group-save-result-title"
      aria-describedby="group-save-result-message"
    >
      <DialogTitle id="group-save-result-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {outcomeIcon[result.outcome]}
        {result.title}
      </DialogTitle>
      <DialogContent>
        <Typography id="group-save-result-message">{result.message}</Typography>

        {result.changes.length > 0 && (
          <Box component={'ul'} sx={{ mt: 2, mb: 0, pl: 3 }}>
            {result.changes.map((change) => (
              <Typography component={'li'} key={change}>
                {change}
              </Typography>
            ))}
          </Box>
        )}

        {result.problems.length > 0 && (
          <Alert severity={'warning'} sx={{ mt: 2 }}>
            <Box component={'ul'} sx={{ m: 0, pl: 2 }}>
              {result.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {result.outcome === 'unknown' && (
          <Button onClick={onRefresh} variant={'contained'} disableElevation={true} color={'secondary'} size={'large'}>
            Refresh team list
          </Button>
        )}
        <Button onClick={onClose} variant={'contained'} size={'large'}>
          {teamSaved ? 'Done' : 'Back to editing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
